import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcQuotemediaV1Sp500ListQueryServiceSp500ListGet } from '@interxfi/api-client/generated/hooks/exc-quotemedia-v1-sp500-list-query-service-sp500-list-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcQuotemediaV1Sp500ListState } from '@interxfi/types/generated/api';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { QuoteMediaSp500ConstituentsTable } from '../../../../../../../../../../features/vendor/quotemedia/sp500-list/quotemedia-sp500-constituents-table';
import {
  QuoteMediaSp500ListEventDrawer,
  QuoteMediaSp500ListEventSummary,
} from '../../../../../../../../../../features/vendor/quotemedia/sp500-list/quotemedia-sp500-list-event';
import { getQuoteMediaSp500ListStateListItems } from '../../../../../../../../../../features/vendor/quotemedia/sp500-list/quotemedia-sp500-list-list-items';
import { getQuotemediaSp500ListDetailPath } from '../../../../../../../../../../features/vendor/quotemedia/sp500-list/quotemedia-sp500-list-routing';
import { QuotemediaSp500ListStatusPill } from '../../../../../../../../../../features/vendor/quotemedia/sp500-list/quotemedia-sp500-list-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/quote-media/sp-500/_sp-500/$quarter')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `quotemedia-sp-500-list-${params.quarter}`,
        label: params.quarter,
        to: getQuotemediaSp500ListDetailPath(params.quarter),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('QuoteMedia S&P 500 List'),
  component: QuoteMediaSp500ListDetail,
});

function getListItems(data: ExcQuotemediaV1Sp500ListState | undefined) {
  const items = getQuoteMediaSp500ListStateListItems(data);

  return {
    general: {
      left: [items.quarter, items.constituentCount],
      right: [items.metadata.createdAt, items.metadata.updatedAt],
    },
  };
}

function getMenuItems(quarter: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: QuoteMediaSp500ListEventDrawer,
        props: { request: { quarter } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load S&P 500 list', 'view this S&P 500 list');

function QuoteMediaSp500ListDetail() {
  const { quarter } = Route.useParams();
  const request = { quarter } as const;
  const { data, error, isPending } = useExcQuotemediaV1Sp500ListQueryServiceSp500ListGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sp500List), [data?.sp500List]);
  const menuItems = useMemo(() => getMenuItems(quarter), [quarter]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <QuotemediaSp500ListStatusPill isPending={showPending} size="large" status={data?.sp500List?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <QuoteMediaSp500ListEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <QuoteMediaSp500ConstituentsTable
          constituents={data?.sp500List?.data?.constituents ?? STABLE_EMPTY_ARRAY_REF}
          headerActions={<Typography.Level3>Constituents</Typography.Level3>}
          showSkeleton={showPending}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
